import * as React from "react";
import {
  Input,
  Dropdown,
  Option,
  Text,
  Button,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Search24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { MAIN_DISCUSSION_TYPES, REVIEW_POINT_TAGS, NOTES_TAGS, findTypeById } from "../types/discussionTypes";
import { StatusItemDto } from "../../../services/status-items.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

// Combine all tags for the filter dropdown
const ALL_TAGS = [...REVIEW_POINT_TAGS, ...NOTES_TAGS];

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  filtersRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchInput: {
    flex: "1 1 200px",
    minWidth: "200px",
  },
  dropdown: {
    minWidth: "150px",
    maxWidth: "200px",
  },
});

interface DiscussionFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  tagFilter: string;
  onTagFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  statusItemFilter: string;
  onStatusItemFilterChange: (value: string) => void;
  statusItems: StatusItemDto[];
  sortBy: string;
  onSortByChange: (value: string) => void;
  resultCount: number;
  onClearFilters: () => void;
}

const DiscussionFilters: React.FC<DiscussionFiltersProps> = ({
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  tagFilter,
  onTagFilterChange,
  statusFilter,
  onStatusFilterChange,
  statusItemFilter,
  onStatusItemFilterChange,
  statusItems,
  sortBy,
  onSortByChange,
  resultCount,
  onClearFilters,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  // Visible items only, sorted alphabetically by name. Hidden items are excluded
  // from the dropdown but the lookup in `getStatusItemDisplayValue` still uses
  // the full `statusItems` list so a previously-set filter pointing at a now-hidden
  // item still renders its name correctly.
  const visibleStatusItems = React.useMemo(
    () =>
      statusItems
        .filter((item) => !item.isHidden)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [statusItems]
  );

  // Check if any filters are active
  const hasActiveFilters = search !== "" || typeFilter !== "all" || tagFilter !== "all" || statusFilter !== "all" || statusItemFilter !== "all";

  // Get display name for type filter
  const getTypeDisplayValue = () => {
    if (typeFilter === "all") return "All Types";
    const typeOption = findTypeById(typeFilter);
    return typeOption ? typeOption.name : typeFilter;
  };

  // Get display name for tag filter
  const getTagDisplayValue = () => {
    if (tagFilter === "all") return "All Tags";
    const tagOption = findTypeById(tagFilter);
    return tagOption ? tagOption.name : tagFilter;
  };

  // Get display name for status filter
  const getStatusDisplayValue = () => {
    if (statusFilter === "all") return "All Status";
    if (statusFilter === "open") return "Open";
    if (statusFilter === "in_progress") return "In Progress";
    if (statusFilter === "closed") return "Closed";
    return statusFilter;
  };

  // Get display name for status item filter
  const getStatusItemDisplayValue = () => {
    if (statusItemFilter === "all") return "All Items";
    const item = statusItems.find((item) => item.id.toString() === statusItemFilter);
    return item ? item.name : statusItemFilter;
  };

  // Get display name for sort filter
  const getSortDisplayValue = () => {
    if (sortBy === "newest") return "Newest First";
    if (sortBy === "oldest") return "Oldest First";
    if (sortBy === "updated") return "Recently Updated";
    if (sortBy === "type") return "Type";
    return sortBy;
  };

  return (
    <div className={styles.container}>
      <div className={styles.filtersRow}>
        <Input
          className={styles.searchInput}
          contentBefore={<Search24Regular />}
          placeholder="Search discussions..."
          value={search}
          onChange={(_e, data) => onSearchChange(data.value)}
        />

        <Dropdown
          className={styles.dropdown}
          placeholder="Type"
          value={getTypeDisplayValue()}
          onOptionSelect={(_e, data) => onTypeFilterChange(data.optionValue as string)}
        >
          <Option value="all">All Types</Option>
          {MAIN_DISCUSSION_TYPES.map((type) => (
            <Option key={type.id} value={type.id}>
              {type.name}
            </Option>
          ))}
        </Dropdown>

        <Dropdown
          className={styles.dropdown}
          placeholder="Tag"
          value={getTagDisplayValue()}
          onOptionSelect={(_e, data) => onTagFilterChange(data.optionValue as string)}
        >
          <Option value="all">All Tags</Option>
          {ALL_TAGS.map((tag) => (
            <Option key={tag.id} value={tag.id}>
              {tag.name}
            </Option>
          ))}
        </Dropdown>

        <Dropdown
          className={styles.dropdown}
          placeholder="Status"
          value={getStatusDisplayValue()}
          onOptionSelect={(_e, data) => onStatusFilterChange(data.optionValue as string)}
        >
          <Option value="all">All Status</Option>
          <Option value="open">Open</Option>
          <Option value="in_progress">In Progress</Option>
          <Option value="closed">Closed</Option>
        </Dropdown>

        <Dropdown
          className={styles.dropdown}
          placeholder="Status Item"
          value={getStatusItemDisplayValue()}
          onOptionSelect={(_e, data) => onStatusItemFilterChange(data.optionValue as string)}
        >
          <Option value="all">All Items</Option>
          {visibleStatusItems.map((item) => (
            <Option key={item.id} value={item.id.toString()}>
              {item.name}
            </Option>
          ))}
        </Dropdown>

        <Dropdown
          className={styles.dropdown}
          placeholder="Sort"
          value={getSortDisplayValue()}
          onOptionSelect={(_e, data) => onSortByChange(data.optionValue as string)}
        >
          <Option value="newest">Newest First</Option>
          <Option value="oldest">Oldest First</Option>
          <Option value="updated">Recently Updated</Option>
          <Option value="type">Type</Option>
        </Dropdown>

        {hasActiveFilters && (
          <Button
            appearance="subtle"
            icon={<Dismiss24Regular />}
            onClick={onClearFilters}
          >
            Clear Filters
          </Button>
        )}
      </div>

      <Text className={typography.caption}>
        Showing {resultCount} {resultCount === 1 ? "discussion" : "discussions"}
      </Text>
    </div>
  );
};

export default DiscussionFilters;
